import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query';
import { getKeycloak, withVerifiedToken } from '../auth/keycloak';
import { can, toUserRoles, type VehicleCapability } from '../auth/roles';
import { UserRole } from '../auth/types';
import { useAuth } from '../auth/useAuth';
import { createGraphQLClient } from '../lib/graphql-client';
import { UPDATE_MOBILIST_STATUS_RESERVED, UPDATE_MOBILIST_USE_FOR_AD } from '../lib/queries';
import { OVERRIDE_HISTORY_QUERY_KEY } from './useVehicleOverrideHistory';
import { VEHICLE_SEARCH_QUERY_KEY } from './useVehicleSearch';
import type { MobilistOverrideNote, UseForAdSetting, VehicleListItem, VehiclesListResponse } from '../types/vehicle';

interface ReserveInput {
  vehicleId: string;
  reserved: boolean;
  reason?: string;
}

interface UseForAdInput {
  vehicleId: string;
  settings: UseForAdSetting[];
  reason?: string;
}

async function updateMobilistField(query: string, input: object, token: string): Promise<void> {
  await createGraphQLClient(token).request(query, { input } as Record<string, unknown>);
}

function assertMutationAccess(token: string | undefined, roles: UserRole[], capability: VehicleCapability): string {
  if (!token) {
    throw new Error('Authentication token is missing. Please sign in again.');
  }

  if (!can(roles, capability)) {
    throw new Error('You are not authorized to perform this action.');
  }

  return token;
}

/**
 * The note the backend just recorded, reconstructed locally.
 *
 * The row is patched rather than refetched, so without this the badge would keep showing the
 * previous reason until the next search. `actor` is the same claim the backend attributes the
 * change to (`preferred_username`), and the timestamp is close enough for a tooltip that only
 * renders a date.
 */
function noteFor(actor: string | undefined, reason?: string): MobilistOverrideNote {
  return { reason: reason?.trim() || null, actor: actor ?? 'unknown-user', at: new Date().toISOString() };
}

/**
 * Whether this submit actually changed the blocked set.
 *
 * The modal posts every (broker, product) cell on every confirm, and the backend records nothing
 * when none of them moved. Stamping a note here regardless would show a reason for a decision the
 * trail does not contain.
 */
function blocksChanged(previous: UseForAdSetting[], submitted: UseForAdSetting[]): boolean {
  const key = (setting: UseForAdSetting): string => `${setting.broker}#${setting.product}`;
  const before = new Set(previous.filter((setting) => !setting.useForAd).map(key));
  const after = new Set(submitted.filter((setting) => !setting.useForAd).map(key));

  return before.size !== after.size || [...after].some((token) => !before.has(token));
}

/** The timeline for this vehicle now has an entry it has not seen. */
function invalidateOverrideHistory(queryClient: ReturnType<typeof useQueryClient>, vehicleId: string): void {
  void queryClient.invalidateQueries({ queryKey: [OVERRIDE_HISTORY_QUERY_KEY, vehicleId] });
}

function patchVehicleInSearchResults(
  queryClient: ReturnType<typeof useQueryClient>,
  vehicleId: string,
  patch: (vehicle: VehicleListItem) => VehicleListItem
): void {
  queryClient.setQueriesData<VehiclesListResponse>({ queryKey: [VEHICLE_SEARCH_QUERY_KEY] }, (current) => {
    if (!current) {
      return current;
    }

    let changed = false;
    const items = current.items.map((item) => {
      if (item.id !== vehicleId) {
        return item;
      }

      changed = true;

      return patch(item);
    });

    if (!changed) {
      return current;
    }

    return {
      ...current,
      items
    };
  });
}

export function useReserveVehicle(): UseMutationResult<void, Error, ReserveInput> {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (input: ReserveInput) => {
      return withVerifiedToken(getKeycloak(), async (freshToken) => {
        const authToken = assertMutationAccess(freshToken, toUserRoles(user?.roles ?? []), 'reserve');

        return updateMobilistField(UPDATE_MOBILIST_STATUS_RESERVED, input, authToken);
      });
    },
    onSuccess: (_, variables) => {
      patchVehicleInSearchResults(queryClient, variables.vehicleId, (vehicle) => ({
        ...vehicle,
        mobilistStatusReserved: variables.reserved,
        mobilistReserveNote: noteFor(user?.name, variables.reason)
      }));
      invalidateOverrideHistory(queryClient, variables.vehicleId);
    }
  });
}

export function useSetVehicleUseForAd(): UseMutationResult<void, Error, UseForAdInput> {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (input: UseForAdInput) => {
      return withVerifiedToken(getKeycloak(), async (freshToken) => {
        const authToken = assertMutationAccess(freshToken, toUserRoles(user?.roles ?? []), 'advertising');

        return updateMobilistField(UPDATE_MOBILIST_USE_FOR_AD, input, authToken);
      });
    },
    onSuccess: (_, variables) => {
      patchVehicleInSearchResults(queryClient, variables.vehicleId, (vehicle) => {
        const changed = blocksChanged(vehicle.mobilistUseForAd, variables.settings);

        return {
          ...vehicle,
          mobilistUseForAd: variables.settings.filter((setting) => setting.useForAd === false),
          mobilistAdBlockNote: changed ? noteFor(user?.name, variables.reason) : vehicle.mobilistAdBlockNote
        };
      });
      invalidateOverrideHistory(queryClient, variables.vehicleId);
    }
  });
}
